import React from 'react';
import { Body5, Body7, Container } from '@ui';
import { SelectableCard } from '@ui/SelectableCard';

import { TransparentFlatButton } from '@ui/buttons';
import { SpecEditorContext } from './SpecEditorContext';
import {
  implementedKindsFor,
  kindEntry,
  RELATIONSHIP_KINDS,
  KINDS_WITH_DATE_RANGE,
  KINDS_WITH_DOC_TYPE_RULES,
  KINDS_WITH_DOC_TYPE_STRINGS,
  DOCUMENT_KIND_PARAMS,
  dateProvidesFor,
} from './specSchema';
import { DocTypeSelect } from './DocTypeSelect';
import { DocTypeRulesEditor } from './DocTypeRulesEditor';
import { DateRangeControl } from './DateRangeControl';
import { Consequence, MicroLabel, Row, Stack } from './styles';

// Question 2 — what the model sees. The largest structural-prevention payload in the editor.
//
// The document-kind cards are disabled against the CONTRACT RAIL's Provides list, with the unmet
// requirement printed on the card. That is the same list the anchor select is built from, so two
// separate load-time failures collapse into one piece of wiring.
//
// Three mechanics get stated where they are set, because all three read backwards:
//   * choosing `related_filter` scores the trigger company from OTHER companies' documents;
//   * `include_self: false` means the scored company's own filings are absent from its own score;
//   * `depth` is pinned to 1, which is all the engine accepts, so it is not offered as a choice.
//
// Switching kind is a REPLACEMENT, not a merge. Every documents resolver's `Params` model is
// `extra: "forbid"` and the loader passes every non-`kind` key into it, so a param left behind from
// the kind you just left does not sit inert — it makes the spec uncompilable, and the form cannot
// show it to you because the form only renders the current kind's controls. What survives a switch
// is exactly the intersection of the two kinds' param sets, which keeps the filter <-> related_filter
// comparison free without making the other switches lossy.

// `doc_types` is the same KEY under three kinds but not the same SHAPE: filter and related_filter
// take rule objects, screen takes bare strings. Carrying it across that boundary produces a value
// no control can render and the loader rejects, so it is re-seeded rather than kept.
const docTypesShape = (kind) => {
  if (KINDS_WITH_DOC_TYPE_RULES.indexOf(kind) > -1) return 'rules';
  if (KINDS_WITH_DOC_TYPE_STRINGS.indexOf(kind) > -1) return 'strings';
  return null;
};

const defaultsFor = (kind, anchorableFields) => {
  // An anchored window with no anchor key is the exact state the date control treats as
  // unreachable, so a seeded default must never be in it: anchor on a date the trigger provides,
  // or seed a now-relative window when it provides none.
  const window = anchorableFields.length
    ? { from: 'T-32d', to: 'T-2d', anchor: `trigger.${anchorableFields[0]}` }
    : { from: 'now-32d', to: 'now-2d' };

  if (kind === 'related_filter') {
    return {
      doc_types: [{ doc_type: '', latest_n: 1 }],
      date_range: window,
      companies: { kind: 'peers_of_trigger', relationship_kinds: ['peer'], include_self: false, depth: 1 },
    };
  }
  if (kind === 'filter') return { doc_types: [{ doc_type: '' }] };
  if (kind === 'screen') return { doc_types: [''], date_range: window };
  return {};
};

export const WhatPanel = () => {
  const { definition, setValue, removeValue, provides } = React.useContext(SpecEditorContext);

  const documentSet = (definition || {}).document_set || {};
  const documents = documentSet.documents || {};
  const kind = documents.kind;
  const scope = documents.companies || {};

  const onPickKind = (nextKind) => {
    if (nextKind === kind) return;

    const allowed = DOCUMENT_KIND_PARAMS[nextKind] || [];
    const outgoingShape = docTypesShape(kind);
    const incomingShape = docTypesShape(nextKind);
    const shapeChanged = !!outgoingShape && !!incomingShape && outgoingShape !== incomingShape;

    // Drop what the incoming kind cannot accept, plus doc_types when the two kinds disagree about
    // its shape. Everything else — notably doc_types across filter <-> related_filter — survives.
    Object.keys(documents).forEach((key) => {
      if (key === 'kind') return;
      const staleParam = allowed.indexOf(key) === -1;
      if (staleParam || (key === 'doc_types' && shapeChanged)) {
        removeValue(`document_set.documents.${key}`);
      }
    });

    setValue('document_set.documents.kind', nextKind);

    const defaults = defaultsFor(nextKind, dateProvidesFor((definition.trigger || {}).kind));
    Object.keys(defaults).forEach((key) => {
      const survives = allowed.indexOf(key) > -1 && documents[key] !== undefined;
      if (!survives || (key === 'doc_types' && shapeChanged)) {
        setValue(`document_set.documents.${key}`, defaults[key]);
      }
    });
  };

  const toggleRelationship = (relationship) => {
    const current = Array.isArray(scope.relationship_kinds) ? scope.relationship_kinds : [];
    const next =
      current.indexOf(relationship) > -1 ? current.filter((r) => r !== relationship) : current.concat(relationship);
    setValue('document_set.documents.companies.relationship_kinds', next);
  };

  const screenDocTypes = Array.isArray(documents.doc_types) ? documents.doc_types : [];

  return (
    <div>
      <MicroLabel>Which companies get scored</MicroLabel>
      <Container top={16}>
        <Stack>
          {implementedKindsFor('companies').map((entry) => (
            <SelectableCard key={entry.kind} selected={(documentSet.companies || {}).kind === entry.kind}>
              <input
                type="radio"
                name="companies-kind"
                checked={(documentSet.companies || {}).kind === entry.kind}
                onChange={() => setValue('document_set.companies.kind', entry.kind)}
              />
              <div>
                <Body5 bold>{entry.kind}</Body5>
                <Container top={2}>
                  <Body7 isLowContrast>{entry.blurb}</Body7>
                </Container>
              </div>
            </SelectableCard>
          ))}
        </Stack>
      </Container>

      <Container top={24}>
        <MicroLabel>Which documents the model reads, per company</MicroLabel>
        <Container top={16}>
          <Stack>
            {implementedKindsFor('documents').map((entry) => {
              // Kept disabled-with-a-reason rather than hidden: unlike an unavailable kind, this is
              // a constraint the operator can actually resolve by changing the trigger above.
              const unmet = (entry.requires || []).filter((r) => provides.indexOf(r) === -1);
              const disabled = unmet.length > 0;
              const reason = disabled
                ? `Needs ${unmet.join(', ')} from the trigger, and the trigger you picked provides ${
                    provides.length ? provides.join(', ') : 'nothing'
                  }.`
                : null;
              return (
                <SelectableCard key={entry.kind} selected={kind === entry.kind} disabled={disabled} reason={reason}>
                  <input
                    type="radio"
                    name="documents-kind"
                    checked={kind === entry.kind}
                    disabled={disabled}
                    onChange={() => onPickKind(entry.kind)}
                  />
                  <div>
                    <Body5 bold>{entry.kind}</Body5>
                    <Container top={2}>
                      <Body7 isLowContrast>{entry.blurb}</Body7>
                    </Container>
                    {!disabled && entry.caveat ? (
                      <Container top={4}>
                        <Consequence $tone="warn">{entry.caveat}</Consequence>
                      </Container>
                    ) : null}
                  </div>
                </SelectableCard>
              );
            })}
          </Stack>
        </Container>
      </Container>

      {KINDS_WITH_DOC_TYPE_RULES.indexOf(kind) > -1 && (
        <Container top={24}>
          <DocTypeRulesEditor
            basePath="document_set.documents.doc_types"
            perRelatedCompany={kind === 'related_filter'}
          />
        </Container>
      )}

      {KINDS_WITH_DOC_TYPE_STRINGS.indexOf(kind) > -1 && (
        <Container top={24}>
          <MicroLabel>Document types to search over</MicroLabel>
          <Container top={8}>
            {screenDocTypes.map((type, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Container key={`screen-doc-type-${index}`} bottom={8}>
                <Row $align="flex-start">
                  <DocTypeSelect
                    ariaLabel={`Document type ${index + 1}`}
                    value={typeof type === 'string' ? type : ''}
                    onChange={(v) => setValue(`document_set.documents.doc_types.${index}`, v)}
                    invalid={typeof type !== 'string' || !type}
                    hint={
                      typeof type === 'string'
                        ? undefined
                        : 'This row came from another kind as a rule object. Choosing a type replaces it with a name.'
                    }
                  />
                  <TransparentFlatButton onClick={() => removeValue(`document_set.documents.doc_types.${index}`)}>
                    Remove
                  </TransparentFlatButton>
                </Row>
              </Container>
            ))}
            <TransparentFlatButton
              onClick={() => setValue(`document_set.documents.doc_types.${screenDocTypes.length}`, '')}
            >
              Add a document type
            </TransparentFlatButton>
          </Container>
          <Container top={8}>
            <Consequence>
              A screen searches these types across the whole corpus in the window below. Unlike filter and
              related_filter there is no per-type cap — the budget comes from max_context_chunks.
            </Consequence>
          </Container>
        </Container>
      )}

      {KINDS_WITH_DATE_RANGE.indexOf(kind) > -1 && (
        <Container top={24}>
          <DateRangeControl basePath="document_set.documents.date_range" />
        </Container>
      )}

      {kind === 'related_filter' && (
        <Container top={24}>
          <MicroLabel>Which related companies</MicroLabel>
          <Container top={16}>
            <Row>
              {/* Native checkboxes rather than @ui/inputs/Checkbox: that component renders a styled
                  div with an onClick and no input, so it is not keyboard reachable and exposes no
                  checked state. These sit beside the native radios the kind cards already use. */}
              {RELATIONSHIP_KINDS.map((relationship) => (
                <label key={relationship} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={(scope.relationship_kinds || []).indexOf(relationship) > -1}
                    onChange={() => toggleRelationship(relationship)}
                  />
                  <Body5 as="span">{relationship}</Body5>
                </label>
              ))}
            </Row>
          </Container>
          <Container top={16}>
            <Consequence>
              These are edge kinds in the company-relationships graph. A company with no edges of the kinds you pick is
              skipped loudly rather than quietly widened to the whole corpus.
            </Consequence>
          </Container>

          <Container top={16}>
            <label style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 8 }}>
              <input
                type="checkbox"
                checked={scope.include_self === true}
                onChange={() =>
                  setValue('document_set.documents.companies.include_self', !(scope.include_self === true))
                }
              />
              <Body5 as="span">Also read the scored company’s own documents</Body5>
            </label>
            <Container top={6}>
              <Consequence $tone={scope.include_self === true ? undefined : 'warn'}>
                {scope.include_self === true
                  ? 'On — the company’s own filings are read alongside its related companies’.'
                  : 'Off — the scored company’s OWN filings are NOT part of what the model reads about it.'}
              </Consequence>
            </Container>
          </Container>
        </Container>
      )}

      {kind === 'screen' && (
        <Container top={24}>
          <Consequence $tone="warn">{kindEntry('documents', 'screen').caveat}</Consequence>
        </Container>
      )}

      <Container top={24}>
        <MicroLabel>How companies and documents become events</MicroLabel>
        <Container top={16}>
          <Stack>
            {implementedKindsFor('fanout').map((entry) => (
              <SelectableCard key={entry.kind} selected={(documentSet.fanout || {}).kind === entry.kind}>
                <input
                  type="radio"
                  name="fanout-kind"
                  checked={(documentSet.fanout || {}).kind === entry.kind}
                  onChange={() => setValue('document_set.fanout.kind', entry.kind)}
                />
                <div>
                  <Body5 bold>{entry.kind}</Body5>
                  <Container top={2}>
                    <Body7 isLowContrast>{entry.blurb}</Body7>
                  </Container>
                </div>
              </SelectableCard>
            ))}
          </Stack>
        </Container>
      </Container>
    </div>
  );
};
